import React, { useEffect, useRef, useState } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";

import { useApi } from "../../../contexts/ApiContext";
import { useStores } from "../../../contexts/StoreContext";
import useIsHydrated from "../../hooks/useIsHydrated";
import CustomSelect from "../../CustomSelect/CustomSelect";
import LocationControl from "./LocationControl";
import getPosition from "./getPosition";

function LocationSelect(props) {
  const { onSelect, styles, placeholder, initialValue } = props;
  const { configStore, filtersStore } = useStores();
  const isHydrated = useIsHydrated();

  const api = useApi();
  const { config } = configStore;
  const { appliedFilters } = filtersStore;

  const asyncSelectRef = useRef(null);

  const [geolocationStatusText, setGeolocationStatusText] = useState(null);
  const [isGeolocationInProgress, setIsGeolocationInProgress] = useState(false);

  const handleSetLocation = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setGeolocationStatusText("Wait, we're trying to locate you...");
    setIsGeolocationInProgress(true);

    getPosition()
      .then((geoLocation) => {
        api
          .reverseGeocode({
            lat: geoLocation.coords.latitude,
            long: geoLocation.coords.longitude,
          })
          .then((geocodedLocation) => {
            const address =
              geocodedLocation?.data[0]?.address?.freeformAddress ||
              geocodedLocation?.data[0]?.address?.streetNameAndNumber ||
              "Your localization";

            asyncSelectRef.current.setValue({
              coordsMode: true,
              label: address,
              lat: geoLocation.coords.latitude,
              long: geoLocation.coords.longitude,
              value: address,
            });
            setGeolocationStatusText(null);
            setIsGeolocationInProgress(false);
          });
      })
      .catch((err) => {
        setGeolocationStatusText(
          "We can't reach your location. Maybe check permissions?"
        );
        setIsGeolocationInProgress(false);
        setTimeout(() => {
          setGeolocationStatusText(null);
        }, 5000);
        // eslint-disable-next-line no-console
        console.error("Error message ->", err.message);
      });
  };

  const loadOptions = (inputValue) => {
    if (inputValue.length >= 3) {
      return (
        api
          .searchLocation(inputValue, config.location || "GB")
          .then((response) => {
            return response.data.map((entry) => {
              const label = [
                entry.addressLine1,
                entry.addressLine2,
                entry.addressLine3,
                entry.city,
                entry.county,
                entry.postCode,
              ]
                .filter(Boolean)
                .join(", ");

              return {
                coordsMode: true,
                label,
                lat: entry.lat,
                long: entry.long,
                value: entry,
              };
            });
          })
          // eslint-disable-next-line no-console
          .catch((err) => console.log(err))
      );
    }

    return Promise.resolve();
  };

  useEffect(() => {
    const load = async () => {
      const locationLabel =
        appliedFilters.location?.label ||
        appliedFilters.locationLabel ||
        initialValue;

      if (!isEmpty(locationLabel)) {
        const options = await loadOptions(locationLabel);

        const filteredOption = options.find((location) =>
          location.label.includes(locationLabel)
        );

        if (filteredOption) {
          asyncSelectRef.current.setValue({
            label: filteredOption.label,
            value: filteredOption,
          });
        }
        if (initialValue) {
          onSelect(filteredOption);
        }
      }
    };
    load();
  }, [JSON.stringify(appliedFilters), initialValue]);

  const theme = useTheme();

  useEffect(() => {
    if (!appliedFilters.locationLabel && isEmpty(appliedFilters.location)) {
      asyncSelectRef.current?.clearValue();
    }
  }, [JSON.stringify(appliedFilters)]);

  return (
    <CustomSelect
      aria-label="Location"
      className="location-select"
      components={{
        Control: LocationControl,
        DropdownIndicator: () => null,
      }}
      data-testId="location-select"
      defaultInputValue={initialValue}
      instanceId="locationSelect"
      isAsync
      isClearable
      isGeolocationInProgress={isGeolocationInProgress}
      isSearchable
      loadOptions={loadOptions}
      locationButtonConfig={{ color: theme.primaryColor }}
      menuPortalTarget={isHydrated ? document.body : null}
      noOptionsMessage={() => "Type to start searching"}
      onChange={(value, ...rest) => {
        if (value?.label === initialValue) {
          return null;
        }
        return onSelect(value, rest);
      }}
      onLocationClick={handleSetLocation}
      placeholder={geolocationStatusText || placeholder || "Town or Postcode"}
      selectRef={asyncSelectRef}
      styles={styles}
    />
  );
}

export default observer(LocationSelect);

LocationSelect.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  styles: PropTypes.shape({}),
};

LocationSelect.defaultProps = {
  initialValue: "",
  onSelect: () => {},
  placeholder: "Town or Postcode",
  styles: {},
};
